import moment from 'moment'
import {formatDate} from './time'
import {_L} from '../context/LocaleContext'
/**
 * Scale the day of the week so that Monday has index of 0 and Sunday has index of 6
 * @param date
 */
export const getWeekDay = date => {
  // moment day returns Sunday as day 0 of the week.
  let day = moment(date).day()
  return shiftDay(day)
}

export const shiftDay = day => {
  let shiftedDay = day - 1

  if (shiftedDay < 0) {
    shiftedDay = 6
  }
  return shiftedDay
}

export const getDailyRecurrenceDates = (startDate, endDate) => {
  let dates = []

  let start = moment(startDate)
  let end = moment(endDate)

  while (start.isSameOrBefore(end)) {
    dates.push(start.clone().toDate())
    start.add(1, 'day')
  }

  return dates
}

/**
 * Returns an array of the indexes where the weekDay value is set to 1
 * @param weekDays
 * @returns {Array}
 */
const getWeekDayIndexes = weekDays =>
  weekDays.reduce((acc, item, idx) => {
    if (item === 1) {
      acc.push(idx)
    }
    return acc
  }, [])

export const getWeeklyRecurrenceDates = (startDate, endDate, weekDays) => {
  let start = moment(startDate)
  let end = moment(endDate)

  let dates = []

  let weekDayIndexes = getWeekDayIndexes(weekDays)

  while (start.isSameOrBefore(end)) {
    weekDayIndexes.forEach(weekDayIndex => {
      if (getWeekDay(start) === weekDayIndex) {
        dates.push(start.clone().toDate())
      }
    })

    start.add(1, 'day')
  }

  return dates
}

const decodeFrequencyDay = frequencyDay => {
  const frequencyDayLookup = {
    mon: 0,
    tue: 1,
    wed: 2,
    thu: 3,
    fri: 4,
    sat: 5,
    sun: 6
  }

  return frequencyDayLookup[frequencyDay]
}

const decodeFrequency = frequency => {
  const frequencyLookup = {
    '1st': 0,
    '2nd': 1,
    '3rd': 2,
    '4th': 3,
    last: 4
  }

  return frequencyLookup[frequency]
}

/* eslint no-unmodified-loop-condition: 0 */

export const getMonthRecurrenceDates = (
  startDate,
  endDate,
  frequencyDay,
  frequency
) => {
  let start = moment(startDate).startOf('month')
  let end = moment(endDate).endOf('month')

  // Get the months in the date range
  let months = []
  let monthStart = start.clone()
  let monthEnd = end.clone()
  while (
    monthEnd.isAfter(monthStart) ||
    monthStart.format('M') === monthEnd.format('M')
  ) {
    months.push(monthStart.clone())
    monthStart.add(1, 'month')
  }

  // Initialize the dates keys with empty arrays
  let dates = months.reduce((acc, curVal) => {
    acc[curVal.format('YYYY-MM')] = []
    return acc
  }, {})

  while (start.isSameOrBefore(end)) {
    if (getWeekDay(start) === decodeFrequencyDay(frequencyDay)) {
      let monthKey = start.format('YYYY-MM')

      dates[monthKey].push(start.toDate())
    }
    start.add(1, 'day')
  }

  //
  let monthDates = Object.values(dates).reduce((acc, curVal) => {
    // Get the first, second, third, ... occurrence
    // The last occurrence is a special case
    let decodedFrequency = decodeFrequency(frequency)
    if (decodedFrequency === 4) {
      // Get last
      acc.push(curVal[curVal.length - 1])
    } else {
      acc.push(curVal[decodedFrequency])
    }
    return acc
  }, [])

  return monthDates.filter(date => {
    let dateMoment = moment(date)
    return (
      dateMoment.isSameOrAfter(moment(startDate)) &&
      dateMoment.isSameOrBefore(moment(endDate))
    )
  })
}

export const getRecurrenceValues = ({
  recurrenceType,
  weekDays,
  frequency,
  frequencyDay
}) => {
  let value1 = ''
  let value2 = ''
  let value3 = ''

  switch (recurrenceType) {
    case 'day':
      value1 = `1`
      value2 = ''
      break
    case 'week':
      value1 = weekDays.join(',')
      value2 = '1'
      break
    case 'month':
      value1 = frequency
      value2 = frequencyDay
      value3 = '1'
      break
  }

  return {value1, value2, value3}
}

export const generateRecurrenceXml = ({
  recurrenceType,
  weekDays = [],
  frequency,
  frequencyDay
} = {}) => {
  if (recurrenceType === 'none') {
    return ''
  } else {
    const {value1, value2, value3} = getRecurrenceValues({
      recurrenceType,
      weekDays,
      frequency,
      frequencyDay
    })
    return `<recurring type="${recurrenceType}" value1="${value1}" value2="${value2}" value3="${value3}" total=""/>`
  }
}

/**
 * Returns the list data used in the recurrence components.
 * This function needs to execute after the locale scripts are loaded to allow
 * the labels to be localized
 * @returns {{TYPES: *[], DAYS: *[], FREQUENCY: *[]}}
 */
export const getRecurrenceLists = ({localeState}) => {
  const TYPES = [
    {value: 'none', label: _L({key: 'None', localeState})},
    {value: 'day', label: _L({key: 'Daily', localeState})},
    {value: 'week', label: _L({key: 'Weekly', localeState})},
    {value: 'month', label: _L({key: 'Monthly', localeState})}
  ]

  const DAYS = [
    {value: 'mon', label: _L({key: 'Monday', localeState})},
    {value: 'tue', label: _L({key: 'Tuesday', localeState})},
    {value: 'wed', label: _L({key: 'Wednesday', localeState})},
    {value: 'thu', label: _L({key: 'Thursday', localeState})},
    {value: 'fri', label: _L({key: 'Friday', localeState})},
    {value: 'sat', label: _L({key: 'Saturday', localeState})},
    {value: 'sun', label: _L({key: 'Sunday', localeState})}
  ]

  const FREQUENCY = [
    {value: '1st', label: _L({key: 'first', localeState})},
    {value: '2nd', label: _L({key: 'second', localeState})},
    {value: '3rd', label: _L({key: 'third', localeState})},
    {value: '4th', label: _L({key: 'fourth', localeState})},
    {value: 'last', label: _L({key: 'last', localeState})}
  ]

  return {
    TYPES,
    DAYS,
    FREQUENCY
  }
}

export const getDayParts = ({localeState}) => ({
  0: _L({key: 'Full Day', localeState}),
  1: _L({key: 'Morning', localeState}),
  2: _L({key: 'Afternoon', localeState})
})

const getDayNamesFromWeekDays = ({weekDays, localeState}) => {
  const {DAYS} = getRecurrenceLists({localeState})

  let days = weekDays.reduce((acc, weekDay, idx) => {
    if (weekDay) {
      acc.push(DAYS[idx].label)
    }
    return acc
  }, [])

  return days.join(', ')
}

const getMonthFrequencyText = ({frequency, frequencyDay, localeState}) => {
  const {DAYS, FREQUENCY} = getRecurrenceLists({localeState})
  let dayName = DAYS.filter(item => item.value === frequencyDay)
  let frequencyName = FREQUENCY.filter(item => item.value === frequency)
  return `${frequencyName[0].label} ${dayName[0].label}`
}

export const formatRecurrenceText = ({
  recurrenceType = 'none',
  weekDays = [],
  frequency = 0,
  frequencyDay = 'mon',
  startDate,
  localeState
} = {}) => {
  let recurrenceText = ''

  switch (recurrenceType) {
    case 'none':
      recurrenceText = formatDate(startDate, 'MMM D, YYYY')
      break
    case 'day':
      recurrenceText = _L({key: 'Daily', localeState})
      break
    case 'week':
      recurrenceText =
        _L({key: 'Weekly', localeState}) +
        ': ' +
        getDayNamesFromWeekDays({weekDays, localeState})
      break
    case 'month':
      recurrenceText =
        _L({key: 'Monthly', localeState}) +
        ': ' +
        getMonthFrequencyText({frequency, frequencyDay, localeState})
      break
  }

  return recurrenceText
}

export const getDayRange = (
  recurrenceType,
  startDate,
  endDate,
  weekDays,
  frequencyDay = 'mon',
  frequency = 1
) => {
  let dayRange = []

  switch (recurrenceType) {
    case 'none':
      dayRange.push(startDate)
      break
    case 'day':
      dayRange = getDailyRecurrenceDates(startDate, endDate)
      break
    case 'week':
      dayRange = getWeeklyRecurrenceDates(startDate, endDate, weekDays)
      break
    case 'month':
      dayRange = getMonthRecurrenceDates(
        startDate,
        endDate,
        frequencyDay,
        frequency
      )
      break
  }

  return dayRange
}
export const getSearchTimes = (recurrence, meeting, searchFilter) => {
  let result = {
    searchTimes: [],
    dateRange: []
  }

  if (meeting.isNew && searchFilter.time_start && searchFilter.time_end) {
    const {time_start, time_end} = searchFilter

    const {
      date_start,
      date_end,
      frequency,
      frequencyDay,
      weekDays,
      recurrenceType
    } = recurrence

    let dayRange = getDayRange(
      recurrenceType,
      date_start,
      date_end,
      weekDays,
      frequencyDay,
      frequency
    )

    // TODO: use format function
    let searchTimes = dayRange.map(date => {
      let formattedDate = moment(date).format('YYYY-MM-DD')
      let start = moment(
        formattedDate + ' ' + time_start,
        'YYYY-MM-DD HH:mm'
      ).toDate()
      let end = moment(
        formattedDate + ' ' + time_end,
        'YYYY-MM-DD HH:mm'
      ).toDate()

      return {
        start,
        end
      }
    })

    result = {
      searchTimes,
      dateRange: dayRange
    }
  }

  return result
}

export const getRoomSearchParams = ({searchFilter, recurrence, blId, flId}) => {
  const {
    date_start,
    date_end,
    recurrenceType,
    frequency,
    frequencyDay,
    weekDays
  } = recurrence

  const {time_start, time_end, capacity, resources} = searchFilter

  const recurrenceXml = generateRecurrenceXml({
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency
  })

  return {
    date_start: formatDate(date_start, 'YYYY-MM-DD'),
    date_end: formatDate(date_end, 'YYYY-MM-DD'),
    recurrence: recurrenceXml,
    bl_id: blId,
    fl_id: flId,
    time_start,
    time_end,
    capacity,
    resource_std: resources.map(resource_std => ({
      resource_std: resource_std.value
    }))
  }
}
